import * as THREE from 'three';
import Time from '../../../utils/Time.js'
import Camera from './classes/Camera.js';
import Sizes from './classes/Sizes.js';
import Renderer from './classes/Renderer.js';
import World from './classes/World.js';
import Cards from './classes/Cards.js';

export default class FloatingCards{
  static _instance = null

  static getInstance() {
    return FloatingCards._instance || new FloatingCards()
  }

  constructor(options = {}) {
    if (FloatingCards._instance) {
      return FloatingCards._instance
    }

    FloatingCards._instance = this

    // Get container element
    this.container = options.container
    if (!this.container) {
      console.error('Container element #floating-stamps not found')
      return
    }

    // Animation states
    this.isInView = false
    this.hasStartedAppearAnimation = false
    this.hasCompletedAppearAnimation = false

    this.binds()
    this.setup()
    this.initEventListeners()
    this.setupIntersectionObserver()
  }

  setup() {
    this.sizes = new Sizes(this.container)
    this.time = new Time()
    this.scene = new THREE.Scene()
    this.camera = new Camera()
    this.renderer = new Renderer()
    this.cards = new Cards()
    this.world = new World()

    // Add canvas to container
    this.container.appendChild(this.renderer.instance.domElement)
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '50px',
      threshold: 0.1
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.hasStartedAppearAnimation) {
          this.isInView = true;
          this.startAppearAnimation();
        }
        this.isInView = entry.isIntersecting;
      })
    }, options)

    this.observer.observe(this.container)
  }

  startAppearAnimation() {
    if (this.hasStartedAppearAnimation) return;

    this.hasStartedAppearAnimation = true;
    this.world.cardSystem.startAnimation();
  }

  onAppearAnimationComplete() {
    this.hasCompletedAppearAnimation = true;
    this.world.startSimulation();
  }

  update() {
    this.camera.update(this.time.delta)
    this.world.update(this.time.delta)
    this.renderer.render(this.scene, this.camera.instance)
  }

  initEventListeners() {
    this.sizes.on('resize', this.resize)
    this.time.on('tick', this.update)
  }

  resize() {
    this.camera.resize()
    this.renderer.resize()
    this.cards.resize()
    
    // Update physics simulator positions if it exists
    if (this.world?.physicsSimulator) {
      this.world.physicsSimulator._initializeOriginalPositions()
      this.world.physicsSimulator._setupUniforms()
    }
  }

  binds() {
    this.resize = this.resize.bind(this)
    this.update = this.update.bind(this)
  }

  dispose() {
    // Stop all animations and updates first
    this.observer.disconnect()

    // Remove event listeners
    this.sizes.off('resize', this.resize)
    this.time.off('tick', this.update)

    this.time.destroy()

    // Remove canvas from container
    if (this.renderer?.instance?.domElement) {
      this.container.removeChild(this.renderer.instance.domElement)
    }

    // Dispose of GPU computation resources first
    if (this.world.physicsSimulator) {
      // Dispose of original position texture in uniforms
      if (this.world.physicsSimulator.positionUniforms?.originalPositionTexture?.value) {
        this.world.physicsSimulator.positionUniforms.originalPositionTexture.value.dispose()
      }

      // Dispose of GPU compute textures
      if (this.world.physicsSimulator.gpuCompute) {
        // Get current render targets before disposing
        const positionTarget = this.world.physicsSimulator.gpuCompute.getCurrentRenderTarget(this.world.physicsSimulator.positionVariable)
        const velocityTarget = this.world.physicsSimulator.gpuCompute.getCurrentRenderTarget(this.world.physicsSimulator.velocityVariable)
        const debounceTarget = this.world.physicsSimulator.gpuCompute.getCurrentRenderTarget(this.world.physicsSimulator.debounceVariable)

        // Dispose of render targets if they exist
        if (positionTarget?.texture) positionTarget.texture.dispose()
        if (velocityTarget?.texture) velocityTarget.texture.dispose()
        if (debounceTarget?.texture) debounceTarget.texture.dispose()

        // Dispose the GPUCompute
        this.world.physicsSimulator.gpuCompute.dispose()
      }
    }

    // Dispose of card system resources
    if (this.world.cardSystem?.cardMesh) {
      // Remove mesh from scene first
      this.scene.remove(this.world.cardSystem.cardMesh)

      // Dispose of geometry and its attributes
      if (this.world.cardSystem.cardMesh.geometry) {
        Object.values(this.world.cardSystem.cardMesh.geometry.attributes).forEach(attribute => {
          if (attribute.array) attribute.array = null
        })
        this.world.cardSystem.cardMesh.geometry.dispose()
      }

      // Dispose of material and its textures
      if (this.world.cardSystem.cardMesh.material) {
        if (this.world.cardSystem.cardMesh.material.uniforms) {
          if (this.world.cardSystem.cardMesh.material.uniforms.atlasTexture?.value) {
            this.world.cardSystem.cardMesh.material.uniforms.atlasTexture.value.dispose()
          }
          if (this.world.cardSystem.cardMesh.material.uniforms.positionTexture?.value) {
            this.world.cardSystem.cardMesh.material.uniforms.positionTexture.value.dispose()
          }
          // Clear uniform values
          Object.keys(this.world.cardSystem.cardMesh.material.uniforms).forEach(key => {
            this.world.cardSystem.cardMesh.material.uniforms[key].value = null
          })
        }
        this.world.cardSystem.cardMesh.material.dispose()
      }

      // Clear mesh references
      this.world.cardSystem.cardMesh = null
    }

    // Clean up the scene
    while(this.scene.children.length > 0) {
      const child = this.scene.children[0]
      this.scene.remove(child)
      if (child instanceof THREE.Mesh) {
        if (child.geometry) child.geometry.dispose()
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(material => material.dispose())
          } else {
            child.material.dispose()
          }
        }
      }
    }

    // Finally dispose of the renderer
    if (this.renderer?.instance) {
      this.renderer.instance.dispose()
      this.renderer.instance = null
    }

    // Clear instance
    FloatingCards._instance = null
  }
}
